import { z } from 'zod';

import { CS_DRAFT_CATEGORIES } from '../agents/outcomes';
import { FDA_HOLD_TOPIC } from './draft-shadow';

export const AUTO_REPLY_TOPICS = [
  ...CS_DRAFT_CATEGORIES.filter((category) => category !== 'no-reply-needed'),
  FDA_HOLD_TOPIC,
] as const;
export const AutoReplyTopicSchema = z.enum(AUTO_REPLY_TOPICS);
export type AutoReplyTopic = z.infer<typeof AutoReplyTopicSchema>;

export function draftTopic(category: string, fdaHold: boolean): string {
  return category === 'order-status' && fdaHold ? FDA_HOLD_TOPIC : category;
}

export const AUTO_REPLY_SKIP_REASONS = [
  'switched-off',
  'thread-unreadable',
  'forwarded-by-staff',
  'no-member-message',
  'member-wrote-again',
  'teammate-replied',
  'not-open',
  'teammate-replied-recently',
  'conversation-limit',
  'daily-cap',
  'sub-intent-off',
] as const;
export const AutoReplySkipReasonSchema = z.enum(AUTO_REPLY_SKIP_REASONS);
export type AutoReplySkipReason = z.infer<typeof AutoReplySkipReasonSchema>;

export const AUTO_REPLY_DECISIONS = ['sending', 'sent', 'skipped', 'failed'] as const;
export const AutoReplyDecisionSchema = z.enum(AUTO_REPLY_DECISIONS);
export type AutoReplyDecision = z.infer<typeof AutoReplyDecisionSchema>;

export const AUTO_REPLY_DEFAULT_DAILY_CAP = 150;

export const AutoReplyTopicSettingSchema = z.object({
  topic: AutoReplyTopicSchema,
  enabled: z.boolean(),
});
export type AutoReplyTopicSetting = z.infer<typeof AutoReplyTopicSettingSchema>;

// The Intercom team a conversation is assigned to when an auto-sent reply leaves a CS task
// behind, such as a refund to run in Stripe. Null leaves the conversation open and unassigned.
export const IntercomTeamIdSchema = z.string().regex(/^\d{1,20}$/);

export const AutoReplySettingsSchema = z.object({
  enabled: z.boolean(),
  dailyCap: z.number().int().min(0),
  csTaskTeamId: IntercomTeamIdSchema.nullable(),
  topics: z.array(AutoReplyTopicSettingSchema),
});
export type AutoReplySettings = z.infer<typeof AutoReplySettingsSchema>;

export const UpdateAutoReplyGlobalSchema = z
  .object({
    enabled: z.boolean().optional(),
    dailyCap: z.number().int().min(0).max(5000).optional(),
    csTaskTeamId: IntercomTeamIdSchema.nullable().optional(),
  })
  .refine(
    (input) =>
      input.enabled !== undefined ||
      input.dailyCap !== undefined ||
      input.csTaskTeamId !== undefined,
    { message: 'Send enabled, dailyCap or csTaskTeamId' },
  );
export type UpdateAutoReplyGlobal = z.infer<typeof UpdateAutoReplyGlobalSchema>;

export const UpdateAutoReplyTopicSchema = AutoReplyTopicSettingSchema;
export type UpdateAutoReplyTopic = z.infer<typeof UpdateAutoReplyTopicSchema>;

export const AutoReplyTopicStatsSchema = z.object({
  topic: z.string(),
  attempts: z.number().int(),
  sent: z.number().int(),
  skipped: z.number().int(),
  failed: z.number().int(),
  graded: z.number().int(),
  judgedRight: z.number().int(),
  watched: z.number().int(),
  memberWroteBack: z.number().int(),
});
export type AutoReplyTopicStats = z.infer<typeof AutoReplyTopicStatsSchema>;

export const AutoReplySkipCountSchema = z.object({
  reason: AutoReplySkipReasonSchema,
  count: z.number().int(),
});
export type AutoReplySkipCount = z.infer<typeof AutoReplySkipCountSchema>;

export const AutoReplySentSchema = z.object({
  id: z.uuid(),
  topic: z.string(),
  sentAt: z.iso.datetime(),
  conversationUrl: z.string(),
  judgedRight: z.boolean().nullable(),
  judgeReason: z.string().nullable(),
  memberRepliedAt: z.iso.datetime().nullable(),
  error: z.string().nullable(),
});
export type AutoReplySent = z.infer<typeof AutoReplySentSchema>;

export const AutoReplyReportSchema = z.object({
  since: z.iso.datetime(),
  settings: AutoReplySettingsSchema,
  topics: z.array(AutoReplyTopicStatsSchema),
  skips: z.array(AutoReplySkipCountSchema),
  replies: z.array(AutoReplySentSchema),
});
export type AutoReplyReport = z.infer<typeof AutoReplyReportSchema>;
